/**
 * Утилиты для работы с правами доступа
 */
import { ROLE_PERMISSIONS, Permission } from '@/types/employe/employee.common';
/**
 * Проверяет, имеет ли пользователь определенное разрешение
 */
export const hasPermission = (userRole, permission) => {
    if (!userRole) {
        return false;
    }
    const rolePermissions = ROLE_PERMISSIONS[userRole];
    return rolePermissions ? rolePermissions.includes(permission) : false;
};
/**
 * Проверяет, имеет ли пользователь любое из указанных разрешений
 */
export const hasAnyPermission = (userRole, permissions) => {
    if (!userRole || permissions.length === 0) {
        return false;
    }
    return permissions.some(permission => hasPermission(userRole, permission));
};
/**
 * Проверяет, имеет ли пользователь все указанные разрешения
 */
export const hasAllPermissions = (userRole, permissions) => {
    if (!userRole || permissions.length === 0) {
        return false;
    }
    return permissions.every(permission => hasPermission(userRole, permission));
};
/**
 * Возвращает все разрешения для роли
 */
export const getRolePermissions = (userRole) => {
    return ROLE_PERMISSIONS[userRole] || [];
};
/**
 * Проверяет, является ли пользователь администратором
 */
export const isAdmin = (userRole) => {
    return userRole === 'ADMINISTRATOR';
};
/**
 * Проверяет, является ли пользователь диспетчером
 */
export const isDispatcher = (userRole) => {
    return userRole === 'DISPATCHER';
};
/**
 * Проверяет, может ли пользователь редактировать справочники
 */
export const canEditDictionaries = (userRole) => {
    return hasPermission(userRole, Permission.DICTIONARIES_WRITE);
};
/**
 * Проверяет, может ли пользователь удалять записи из справочников
 */
export const canDeleteFromDictionaries = (userRole) => {
    return hasPermission(userRole, Permission.DICTIONARIES_DELETE);
};
/**
 * Проверяет, может ли пользователь только просматривать справочники
 */
export const canOnlyViewDictionaries = (userRole) => {
    return hasPermission(userRole, Permission.DICTIONARIES_READ) &&
        !hasPermission(userRole, Permission.DICTIONARIES_WRITE);
};
/**
 * Получает роль пользователя из объекта Employee
 */
export const getUserRole = (employee) => {
    return employee?.role || null;
};
/**
 * Проверяет, может ли пользователь просматривать чат
 */
export const canAccessChat = (userRole) => {
    return hasPermission(userRole, Permission.CHAT_READ);
};
/**
 * Проверяет, может ли пользователь писать в чат
 */
export const canWriteChat = (userRole) => {
    return hasPermission(userRole, Permission.CHAT_WRITE);
};
/**
 * Может ли пользователь вести корп-компании (создание, реквизиты, привязки, корп-админы)
 */
export const canManageCompanies = (userRole) => {
    return hasPermission(userRole, Permission.COMPANIES_WRITE);
};
/**
 * Может ли пользователь работать с взаиморасчётами компаний (только ADMINISTRATOR, D14)
 */
export const canManageSettlements = (userRole) => {
    return hasPermission(userRole, Permission.SETTLEMENTS_WRITE);
};
/**
 * Проверяет, активен ли сотрудник
 */
export const isEmployeeActive = (employee) => {
    return employee?.isActive === true && employee?.isDeleted === false;
};
/**
 * Проверяет, даёт ли строка employees доступ к оболочке персонала: активный, не удалённый
 * сотрудник с ролью DISPATCHER или ADMINISTRATOR. Единственное место этого правила во фронте
 * (BR-ACC-03; в БД — corp_staff_role(), контракт §3.3).
 */
export const isStaffEmployee = (employee) => {
    return (isEmployeeActive(employee) &&
        (employee?.role === 'DISPATCHER' || employee?.role === 'ADMINISTRATOR'));
};
